import { useCallback } from 'react';

import {
  useErpCareerStudentCompletionsQuery,
  useRecalcStudentCourseSkillsMutation,
  useSaveCourseCompletionMutation,
} from 'core/graphql';

import { ICourseCompletionRow, ISkillMutationResult } from './careerModel';

const EMPTY: ICourseCompletionRow[] = [];

/**
 * ТЗ 0039 — «какие курсы студент прошёл» для карточки участника.
 *
 * Отдаёт не только вердикт, но и ПРИЧИНУ: данные говорят «прошёл», есть
 * диплом, или это решение сотрудника. Без причины кнопка «не засчитывать»
 * выглядит как действие в пустоту — оператор не видит, что именно он
 * перебивает.
 */
export const useCourseCompletions = (studentId?: string) => {
  const { data, loading, refetch } = useErpCareerStudentCompletionsQuery({
    variables: { studentId: studentId ?? '' },
    skip: !studentId,
    fetchPolicy: 'cache-and-network',
  });

  return {
    rows: data?.careerStudentCompletions ?? EMPTY,
    loading,
    refetch,
  };
};

export const useCourseCompletionMutations = (onDone?: () => void) => {
  const [save, saveState] = useSaveCourseCompletionMutation();
  const [recalc, recalcState] = useRecalcStudentCourseSkillsMutation();

  const setCompletion = useCallback(
    async (
      studentId: string,
      courseId: string,
      status: 'completed' | 'revoked',
    ): Promise<ISkillMutationResult> => {
      const { data } = await save({
        variables: { studentId, courseId, status },
      });
      const payload = data?.setCourseCompletion;
      // Мутация сама пересчитывает начисления — иначе оператор нажал кнопку
      // и до ночи ничего не увидел.
      if (payload?.success && onDone) onDone();
      return {
        success: Boolean(payload?.success),
        errors: (payload?.customErrors ?? []).map((item) => ({
          field: item?.field ?? '',
          message: item?.message ?? '',
        })),
      };
    },
    [save, onDone],
  );

  const recalcSkills = useCallback(
    async (studentId: string) => {
      const { data } = await recalc({ variables: { studentId } });
      const payload = data?.syncStudentCourseSkills;
      if (payload?.success && onDone) onDone();
      return {
        success: Boolean(payload?.success),
        granted: payload?.granted ?? 0,
        promoted: payload?.promoted ?? 0,
        demoted: payload?.demoted ?? 0,
      };
    },
    [recalc, onDone],
  );

  return {
    setCompletion,
    recalcSkills,
    pending: saveState.loading || recalcState.loading,
  };
};
